/**
 * Réserver — the équipements this member may book.
 *
 * The server already narrows `amenity.list` for a non-manager to the active
 * amenities they are eligible for; a manager sees the org's whole list here,
 * archived ones included, so this page drops the archived ones itself — an
 * archived équipement cannot be booked and must not look bookable.
 *
 * Each card is a link to the day view (`/reservations/$amenityId`). The
 * header carries the member's own « Mes réservations » with the count of
 * what is still ahead of them on the ORG's clock.
 */
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { CalendarCheck, ChevronRight } from "lucide-react";

import { Button } from "@gately/ui/button";
import { Card } from "@gately/ui/card";
import { EmptyState } from "@gately/ui/empty-state";
import { StatusBadge } from "@gately/ui/status-badge";

import { TEMPLATE_ICONS } from "~/components/booking/amenity-icons";
import {
  amenityCapacityLabel,
  amenityModeLabel,
  isUpcomingBooking,
  orgClock,
} from "~/lib/booking-format";
import { orpc } from "~/lib/orpc";
import { useOrgTimeZone } from "~/lib/use-me";
import { resolvePermissions, usePermissions } from "~/lib/use-permissions";

export const Route = createFileRoute("/_dashboard/reservations/")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(
      orpc.me.get.queryOptions(),
    );
    if (!resolvePermissions(me).hasAmenities) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: "/" });
    }
  },
  component: CataloguePage,
});

function CataloguePage() {
  const timeZone = useOrgTimeZone();
  const { canManageAmenities } = usePermissions();
  const amenities = useQuery(orpc.amenity.list.queryOptions());
  const mine = useQuery(orpc.booking.mine.queryOptions());

  const clock = orgClock(timeZone);
  const upcoming = mine.data?.filter((b) => isUpcomingBooking(b, clock));
  const cards = (amenities.data ?? []).filter((a) => a.status === "active");

  function renderBody() {
    // Data first: a failed REFETCH keeps `data` and must not blank the grid.
    if (amenities.data) {
      if (cards.length === 0) {
        return (
          <Card className="p-0">
            <EmptyState
              icon={<CalendarCheck className="size-5" />}
              title="Aucun équipement n'est encore réservable."
              description={
                canManageAmenities ? (
                  <>
                    Ajoutez une salle, un espace ou un terrain dans{" "}
                    <Link
                      to="/reglages/reservations"
                      className="text-foreground font-medium underline underline-offset-2"
                    >
                      Réglages › Réservations
                    </Link>
                    .
                  </>
                ) : undefined
              }
            />
          </Card>
        );
      }
      return (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {cards.map((a) => {
            const Icon = TEMPLATE_ICONS[a.template];
            return (
              <li key={a.id}>
                <Link
                  to="/reservations/$amenityId"
                  params={{ amenityId: a.id }}
                  className="group focus-visible:ring-ring block h-full rounded-xl focus-visible:ring-2 focus-visible:outline-none"
                >
                  <Card interactive className="flex h-full flex-col gap-4 p-5">
                    <div className="flex items-start gap-3">
                      <span className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-lg">
                        <Icon className="size-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{a.name}</div>
                        {a.locationLabel ? (
                          <div className="text-muted-foreground truncate text-sm">
                            {a.locationLabel}
                          </div>
                        ) : null}
                      </div>
                      <ChevronRight className="text-muted-foreground mt-0.5 size-4 shrink-0 transition-transform group-hover:translate-x-0.5" />
                    </div>
                    <div className="text-muted-foreground mt-auto space-y-1 text-sm">
                      <div>{amenityCapacityLabel(a.capacity)}</div>
                      <div>{amenityModeLabel(a)}</div>
                    </div>
                    {a.approval === "required" ? (
                      <div>
                        <StatusBadge tone="info">
                          Validation requise
                        </StatusBadge>
                      </div>
                    ) : null}
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
      );
    }
    if (amenities.isError) {
      return (
        <Card className="p-0">
          <EmptyState
            title="Impossible de charger les équipements."
            description="Vérifiez votre connexion puis réessayez."
          />
        </Card>
      );
    }
    return (
      <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <li key={i}>
            <Card className="h-36 animate-pulse bg-neutral-100/60" />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="px-7 py-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-foreground text-2xl font-semibold tracking-[-0.02em]">
            Réserver
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Choisissez un équipement pour voir ses disponibilités.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/reservations/mes-reservations">
            Mes réservations
            {upcoming && upcoming.length > 0 ? (
              <span className="bg-muted text-foreground rounded-full px-1.5 text-xs font-medium tabular-nums">
                {upcoming.length}
              </span>
            ) : null}
          </Link>
        </Button>
      </div>
      {renderBody()}
    </div>
  );
}
